import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Megaphone, Plus, Loader2, Mail, Send, MessageSquare, MessageCircle, Eye, Reply, Users2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const CH = { email: { icon: Mail, label: "Email" }, telegram: { icon: Send, label: "Telegram" }, whatsapp: { icon: MessageCircle, label: "WhatsApp" }, sms: { icon: MessageSquare, label: "SMS" } };

export default function Broadcasts() {
  const { activeSubId } = useAuth();
  const [camps, setCamps] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "email", tags: [], subject: "", body: "", template_name: "", template_lang: "en_US", schedule_at: "" });
  const [seg, setSeg] = useState({ total: 0, eligible: 0 });
  const [detail, setDetail] = useState(null);
  const openDetail = async (id) => {
    setDetail({ loading: true });
    try { const { data } = await api.get(`/broadcasts/${id}`); setDetail(data); }
    catch { setDetail(null); }
  };

  const load = useCallback(() => {
    if (!activeSubId) return;
    setLoading(true);
    Promise.all([api.get(`/broadcasts?sub_account_id=${activeSubId}`), api.get(`/contacts/tags?sub_account_id=${activeSubId}`)])
      .then(([b, t]) => { setCamps(b.data); setTags(t.data); setLoading(false); });
  }, [activeSubId]);
  useEffect(() => { load(); }, [load]);

  const previewSeg = useCallback(() => {
    if (!activeSubId) return;
    api.get(`/broadcasts/segment?sub_account_id=${activeSubId}&tag=${form.tags.join(",")}&channel=${form.channel}`)
      .then(({ data }) => setSeg(data));
  }, [activeSubId, form.tags, form.channel]);
  useEffect(() => { if (open) previewSeg(); }, [open, previewSeg]);

  const toggleTag = (t) => setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/broadcasts?sub_account_id=${activeSubId}`, form);
      toast.success(data.status === "scheduled" ? "Campaign scheduled" : `Campaign sent to ${data.campaign.stats.delivered} contacts`);
      setForm({ name: "", channel: "email", tags: [], subject: "", body: "", template_name: "", template_lang: "en_US", schedule_at: "" }); setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="broadcasts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Bulk Outreach</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Broadcasts</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2 font-semibold" data-testid="new-broadcast-button"><Plus className="w-4 h-4" />New campaign</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>New broadcast campaign</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Campaign name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="March promo" data-testid="broadcast-name-input" /></div>
              <div className="space-y-1.5"><Label>Channel</Label>
                <Tabs value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <TabsList>
                    <TabsTrigger value="email" className="gap-1.5 text-xs" data-testid="bc-channel-email"><Mail className="w-3.5 h-3.5" />Email</TabsTrigger>
                    <TabsTrigger value="telegram" className="gap-1.5 text-xs" data-testid="bc-channel-telegram"><Send className="w-3.5 h-3.5" />Telegram</TabsTrigger>
                    <TabsTrigger value="whatsapp" className="gap-1.5 text-xs" data-testid="bc-channel-whatsapp"><MessageCircle className="w-3.5 h-3.5" />WhatsApp</TabsTrigger>
                    <TabsTrigger value="sms" className="gap-1.5 text-xs" data-testid="bc-channel-sms"><MessageSquare className="w-3.5 h-3.5" />SMS</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="space-y-1.5"><Label>Segment by tags <span className="text-muted-foreground font-normal">(none = everyone)</span></Label>
                <div className="flex flex-wrap gap-1.5">
                  {tags.length === 0 && <span className="text-xs text-muted-foreground">No tags yet — will send to everyone.</span>}
                  {tags.map((t) => (
                    <button key={t} onClick={() => toggleTag(t)} type="button"
                      className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${form.tags.includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
                      data-testid={`bc-tag-${t}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm rounded-lg bg-secondary/40 border border-border/60 px-3 py-2" data-testid="segment-preview">
                <Users2 className="w-4 h-4 text-primary" />
                <span className="font-semibold">{seg.eligible}</span>
                <span className="text-muted-foreground">of {seg.total} contacts reachable by {CH[form.channel].label}</span>
              </div>
              {form.channel === "email" && (
                <div className="space-y-1.5"><Label>Subject</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Big news inside…" data-testid="broadcast-subject-input" /></div>
              )}
              {form.channel === "whatsapp" && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-1.5"><Label>Approved template name</Label>
                    <Input value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })} placeholder="spring_offer" className="font-mono text-sm" data-testid="broadcast-template-input" /></div>
                  <div className="space-y-1.5"><Label>Language</Label>
                    <Input value={form.template_lang} onChange={(e) => setForm({ ...form, template_lang: e.target.value })} placeholder="en_US" className="font-mono text-sm" data-testid="broadcast-lang-input" /></div>
                  <p className="col-span-3 text-xs text-muted-foreground">WhatsApp marketing must use a Meta-approved template. We pass each lead's first name as the first variable. Only opted-in leads with a phone are reachable.</p>
                </div>
              )}
              {form.channel !== "whatsapp" && (
                <div className="space-y-1.5"><Label>Message</Label>
                  <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} placeholder="Hi {{first_name}}, …" data-testid="broadcast-body-input" />
                  <p className="text-xs text-muted-foreground">Merge tags: <span className="font-mono">{"{{first_name}}"}</span>, <span className="font-mono">{"{{name}}"}</span>. Emails track opens automatically.</p>
                </div>
              )}
              <div className="space-y-1.5"><Label>Schedule for later <span className="text-muted-foreground font-normal">(optional — leave empty to send now)</span></Label>
                <Input type="datetime-local" value={form.schedule_at} onChange={(e) => setForm({ ...form, schedule_at: e.target.value })} data-testid="broadcast-schedule-input" />
                <p className="text-xs text-muted-foreground">Times are UTC. Scheduled campaigns send automatically via the platform scheduler.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={sending || !form.name || (form.channel === "whatsapp" ? !form.template_name : !form.body) || seg.eligible === 0} className="gap-2" data-testid="send-broadcast-button">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{form.schedule_at ? "Schedule" : `Send to ${seg.eligible}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {camps.length === 0 ? (
        <Card className="p-16 text-center bg-card border-border/70" data-testid="broadcasts-empty">
          <Megaphone className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No campaigns yet. Send your first broadcast to a segment.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {camps.map((c) => {
            const Icon = CH[c.channel]?.icon || Mail;
            const openRate = c.stats.delivered ? Math.round(c.stats.opened / c.stats.delivered * 100) : 0;
            return (
              <Card key={c.id} className="p-5 bg-card border-border/70 cursor-pointer hover:border-primary/40 transition-colors" onClick={() => openDetail(c.id)} data-testid={`broadcast-${c.id}`}>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{CH[c.channel]?.label} · {new Date(c.sent_at).toLocaleString()} {c.tags?.length ? `· ${c.tags.join(", ")}` : "· everyone"}</p>
                  </div>
                  <Badge variant="secondary" className="gap-1">{c.status === "scheduled" ? <><Clock className="w-3 h-3 text-amber-400" />Scheduled</> : <><CheckCircle2 className="w-3 h-3 text-emerald-400" />Sent</>}</Badge>
                </div>
                {c.status === "scheduled" ? (
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-amber-200/90 flex items-center gap-2" data-testid={`scheduled-note-${c.id}`}>
                    <Clock className="w-4 h-4" />Scheduled to send {new Date(c.scheduled_at).toLocaleString()}
                  </div>
                ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[["Delivered", c.stats.delivered, Users2, "#06B6D4"], ["Opened", `${c.stats.opened} (${openRate}%)`, Eye, "#10B981"],
                    ["Replies", c.stats.replied, Reply, "#8B5CF6"], ["Failed", c.stats.failed, MessageSquare, "#F43F5E"]].map(([l, v, I, col]) => (
                    <div key={l} className="rounded-lg border border-border/60 p-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><I className="w-3.5 h-3.5" style={{ color: col }} />{l}</div>
                      <p className="font-display font-bold text-xl mt-1">{v}</p>
                    </div>
                  ))}
                </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="campaign-detail">
          <DialogHeader><DialogTitle>{detail?.loading ? "Loading…" : detail?.name}</DialogTitle></DialogHeader>
          {detail && !detail.loading && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Delivered", detail.stats.delivered], ["Opened", detail.stats.opened], ["Replies", detail.stats.replied], ["Failed", detail.stats.failed]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-border/60 py-2"><p className="text-[10px] uppercase text-muted-foreground">{l}</p><p className="font-display font-bold">{v}</p></div>
                ))}
              </div>
              <div className="rounded-lg border border-border/60 divide-y divide-border/60 max-h-72 overflow-y-auto">
                {(detail.recipient_list || []).length === 0 && <p className="p-4 text-sm text-muted-foreground text-center">No recipients.</p>}
                {(detail.recipient_list || []).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`recipient-${r.id}`}>
                    <span className="flex-1 truncate">{r.contact_name || "Unknown"}</span>
                    {r.opened_at && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">opened</span>}
                    {r.replied_at && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">replied</span>}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${r.status === "failed" ? "bg-rose-500/15 text-rose-400" : "bg-secondary text-muted-foreground"}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
